/**
 * assets/product-form.js
 * Drives every interactive piece of sections/main-product.liquid:
 *  - variant selection (updates price, image, availability)
 *  - one-time vs Subscribe & Save + frequency picker (real selling plans)
 *  - live savings calculation for multipack variants
 *  - quantity stepper
 *  - Ajax add-to-cart (POST /cart/add.js) with error handling
 *  - image gallery thumbnail swapping
 *
 * No dynamic pricing is invented client-side — every price shown comes
 * from the product/variant JSON Shopify already renders into the page
 * (see the <script type="application/json" id="ProductJson-...">
 * block in main-product.liquid) or from a selling plan's own
 * price_adjustments, exactly as Shopify computes it at checkout.
 */
(function () {
  document.querySelectorAll("[data-product-form]").forEach(initProductForm);

  function initProductForm(form) {
    const section = form.closest("[data-section-id]");
    if (!section) return;

    const productJsonEl = section.querySelector('script[id^="ProductJson-"]');
    if (!productJsonEl) return;
    const product = JSON.parse(productJsonEl.textContent);
    const bagCountsEl = section.querySelector("[data-bag-counts]");
    const bagCounts = bagCountsEl ? JSON.parse(bagCountsEl.textContent) : {};

    const variantIdInput = form.querySelector("[data-variant-id]");
    const qtyInput = form.querySelector("[data-quantity-input]");
    const addBtn = form.querySelector("[data-add-to-cart]");
    const addBtnText = form.querySelector("[data-add-to-cart-text]");
    const addBtnPrice = form.querySelector("[data-add-to-cart-price]");
    const errorBox = form.querySelector("[data-form-error]");
    const sellingPlanInput = form.querySelector("[data-selling-plan-input]");

    const selectedOptions = {};
    product.options.forEach((name, i) => {
      selectedOptions[i] = product.variants.find((v) => v.available) ? findInitialValue(i) : product.options_with_values?.[i]?.values?.[0];
    });

    function findInitialValue(i) {
      const firstAvailable = product.variants.find((v) => v.available) || product.variants[0];
      return firstAvailable ? firstAvailable.options[i] : null;
    }

    function currentVariant() {
      return (
        product.variants.find((v) =>
          product.options.every((_, i) => v.options[i] === selectedOptions[i])
        ) || product.variants[0]
      );
    }

    function formatMoney(cents) {
      // Falls back to a plain $ format if Shopify's money filter isn't reachable client-side.
      return "$" + (cents / 100).toFixed(2);
    }

    function updateVariantUI() {
      const variant = currentVariant();
      if (!variant) return;
      variantIdInput.value = variant.id;

      section.querySelectorAll("[data-option-value]").forEach((btn) => {
        const idx = Number(btn.dataset.optionIndex);
        btn.classList.toggle("active", btn.dataset.value === selectedOptions[idx]);
        btn.setAttribute("aria-pressed", btn.dataset.value === selectedOptions[idx] ? "true" : "false");
      });

      const priceEl = section.querySelector("[data-price-amount]");
      if (priceEl) priceEl.textContent = formatMoney(variant.price);

      const compareEl = section.querySelector("[data-compare-at-price]");
      if (compareEl) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          compareEl.textContent = formatMoney(variant.compare_at_price);
          compareEl.hidden = false;
        } else {
          compareEl.hidden = true;
        }
      }

      const perBagEl = section.querySelector("[data-per-bag-price]");
      const bagCount = bagCounts[variant.id] || 1;
      if (perBagEl) {
        if (bagCount > 1) {
          perBagEl.textContent = "(" + formatMoney(variant.price / bagCount) + "/bag)";
          perBagEl.hidden = false;
        } else {
          perBagEl.hidden = true;
        }
      }

      if (variant.featured_image && variant.featured_image.src) {
        const mainImg = section.querySelector("#ProductMainImage");
        if (mainImg) mainImg.src = variant.featured_image.src;
      }

      refreshAddToCartState(variant);
      const activePlanBtn = sellingPlanInput && sellingPlanInput.value
        ? section.querySelector(`[data-selling-plan-id="${sellingPlanInput.value}"]`)
        : null;
      if (activePlanBtn) activePlanBtn.click();
    }

    const sticky = section.querySelector("[data-sticky-atc]");
    const stickyBtn = sticky && sticky.querySelector("[data-sticky-add]");
    const stickyPrice = sticky && sticky.querySelector("[data-sticky-price]");
    const stickyVariant = sticky && sticky.querySelector("[data-sticky-variant]");

    function refreshAddToCartState(variant) {
      const available = variant && variant.available;
      addBtn.disabled = !available;
      if (addBtnText) addBtnText.textContent = available ? "Add to Cart" : "Sold Out";
      if (stickyBtn) {
        stickyBtn.disabled = !available;
        stickyBtn.textContent = available ? "Add to Cart" : "Sold Out";
      }
      if (stickyVariant && variant) {
        const label = variant.title === "Default Title" ? "" : variant.title + " · ";
        stickyVariant.innerHTML = "";
        stickyVariant.append(document.createTextNode(label));
        const span = document.createElement("span");
        span.setAttribute("data-sticky-price", "");
        span.textContent = formatMoney(variant.price);
        stickyVariant.append(span);
      }
      updateAddToCartPrice(variant);
    }

    function updateAddToCartPrice(variant) {
      if (!addBtnPrice || !variant) return;
      const qty = Math.max(1, parseInt(qtyInput.value, 10) || 1);
      const planId = sellingPlanInput ? sellingPlanInput.value : "";
      let unitPrice = variant.price;

      if (planId) {
        const plan = findSellingPlan(planId);
        if (plan && plan.price) unitPrice = plan.price;
      }
      addBtnPrice.textContent = "— " + formatMoney(unitPrice * qty);
    }

    function findSellingPlan(id) {
      const variant = currentVariant();
      const allocation = (variant.selling_plan_allocations || []).find((a) => String(a.selling_plan_id) === String(id));
      return allocation ? { price: allocation.price } : null;
    }

    // --- Option buttons ---
    section.querySelectorAll("[data-option-value]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const idx = Number(btn.dataset.optionIndex);
        selectedOptions[idx] = btn.dataset.value;
        updateVariantUI();
        document.dispatchEvent(
          new CustomEvent("dyet:select_item", { detail: { product_id: product.id, variant_id: currentVariant().id } })
        );
      });
    });

    // --- Purchase type (one-time vs subscription) ---
    form.querySelectorAll('[name="purchase_option"]').forEach((radio) => {
      radio.addEventListener("change", () => {
        const isSubscription = radio.value === "subscription" && radio.checked;
        const optionsRow = section.querySelector("[data-selling-plan-options]");
        const priceDisplay = section.querySelector("[data-selling-plan-price-display]");

        if (isSubscription) {
          if (optionsRow) optionsRow.hidden = false;
          const firstPlanBtn = section.querySelector("[data-selling-plan-id]");
          if (firstPlanBtn) firstPlanBtn.click();
        } else {
          if (optionsRow) optionsRow.hidden = true;
          if (priceDisplay) priceDisplay.hidden = true;
          if (sellingPlanInput) sellingPlanInput.value = "";
          updateAddToCartPrice(currentVariant());
        }
      });
    });

    section.querySelectorAll("[data-selling-plan-id]").forEach((btn) => {
      btn.addEventListener("click", () => {
        section.querySelectorAll("[data-selling-plan-id]").forEach((b) => b.classList.remove("active"));
        btn.classList.add("active");
        sellingPlanInput.value = btn.dataset.sellingPlanId;

        const priceDisplay = section.querySelector("[data-selling-plan-price-display]");
        const variant = currentVariant();
        const plan = findSellingPlan(btn.dataset.sellingPlanId);
        if (priceDisplay && plan) {
          priceDisplay.hidden = false;
          priceDisplay.textContent =
            formatMoney(plan.price) + " / delivery — you save " + formatMoney(variant.price - plan.price) + " per order";
        }
        updateAddToCartPrice(variant);
      });
    });

    // --- Quantity stepper ---
    const stepDown = form.querySelector('[data-step="down"]');
    const stepUp = form.querySelector('[data-step="up"]');
    if (stepDown) stepDown.addEventListener("click", () => {
      qtyInput.value = Math.max(1, (parseInt(qtyInput.value, 10) || 1) - 1);
      updateAddToCartPrice(currentVariant());
    });
    if (stepUp) stepUp.addEventListener("click", () => {
      qtyInput.value = (parseInt(qtyInput.value, 10) || 1) + 1;
      updateAddToCartPrice(currentVariant());
    });
    qtyInput.addEventListener("change", () => updateAddToCartPrice(currentVariant()));

    // --- Ajax add to cart ---
    form.addEventListener("submit", async (e) => {
      e.preventDefault();
      errorBox.hidden = true;
      addBtn.disabled = true;
      const originalText = addBtnText ? addBtnText.textContent : "";
      if (addBtnText) addBtnText.textContent = "Adding…";

      const formData = new FormData(form);
      const payload = {
        id: formData.get("id"),
        quantity: parseInt(formData.get("quantity"), 10) || 1,
      };
      const sellingPlan = formData.get("selling_plan");
      if (sellingPlan) payload.selling_plan = sellingPlan;

      try {
        const res = await fetch(window.driedYet.settings.cartAddUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify(payload),
        });
        const data = await res.json();

        if (!res.ok) {
          throw new Error(data.description || data.message || "Couldn't add this item to your cart.");
        }

        document.dispatchEvent(
          new CustomEvent("dyet:add_to_cart", {
            detail: { product_id: product.id, variant_id: payload.id, quantity: payload.quantity, selling_plan: sellingPlan || null },
          })
        );
        document.dispatchEvent(new CustomEvent("dyet:cart:added"));

        if (addBtnText) addBtnText.textContent = "Added ✓";
        setTimeout(() => {
          if (addBtnText) addBtnText.textContent = originalText;
        }, 1800);
      } catch (err) {
        errorBox.textContent = err.message;
        errorBox.hidden = false;
        if (addBtnText) addBtnText.textContent = originalText;
      } finally {
        addBtn.disabled = false;
      }
    });

    // --- Gallery thumbnails (images + video) ---
    section.querySelectorAll("[data-thumb]").forEach((thumb) => {
      thumb.addEventListener("click", () => {
        section.querySelectorAll("[data-thumb]").forEach((t) => t.classList.remove("active"));
        thumb.classList.add("active");
        const mainImg = section.querySelector("#ProductMainImage");
        section.querySelectorAll("[data-media-video]").forEach((wrap) => {
          const on = wrap.dataset.mediaVideo === thumb.dataset.videoId;
          wrap.hidden = !on;
          const video = wrap.querySelector("video");
          if (video) on ? video.play().catch(() => {}) : video.pause();
        });
        if (thumb.dataset.videoId) {
          if (mainImg) mainImg.hidden = true;
        } else if (mainImg) {
          mainImg.hidden = false;
          mainImg.removeAttribute("srcset");
          mainImg.src = thumb.dataset.fullSrc;
        }
      });
    });

    // --- Mobile sticky Add to Cart: visible once the main button scrolls out of view ---
    if (sticky && stickyBtn && "IntersectionObserver" in window) {
      sticky.hidden = false;
      const io = new IntersectionObserver(([entry]) => {
        const past = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        sticky.classList.toggle("is-visible", past);
      });
      io.observe(addBtn);
      stickyBtn.addEventListener("click", () => {
        if (typeof form.requestSubmit === "function") form.requestSubmit();
        else addBtn.click();
      });
    }

    // --- Estimated delivery (business days from Theme settings) ---
    const est = section.querySelector("[data-delivery-estimate]");
    if (est) {
      const addBusinessDays = (date, n) => {
        const d = new Date(date);
        let added = 0;
        while (added < n) {
          d.setDate(d.getDate() + 1);
          const day = d.getDay();
          if (day !== 0 && day !== 6) added++;
        }
        return d;
      };
      const fmt = (d) => d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
      const min = parseInt(est.dataset.minDays, 10);
      const max = parseInt(est.dataset.maxDays, 10);
      if (min > 0 && max >= min) {
        const now = new Date();
        est.querySelector("[data-delivery-range]").textContent = `${fmt(addBusinessDays(now, min))} – ${fmt(addBusinessDays(now, max))}`;
        est.hidden = false;
      }
    }

    document.dispatchEvent(new CustomEvent("dyet:view_item", { detail: { product_id: product.id } }));
    updateVariantUI();
  }
})();
